import { EContractorPaymentStatus } from "../../contractor-payments";
import { schemes, zSchema } from "../../schema-validation";
import { EContractorDisbursementMethod } from "../../workers-shared";
import { EWebhookType } from "../EWebhookType";

/**
 * Shape of a single payment item included in contractor payment webhooks.
 * Kept permissive (passthrough) so the full payment item adapter payload can be
 * forwarded without coupling this schema to every payment item field.
 */
export const contractorPaymentWebhookPaymentItemSchema = zSchema
  .object({
    paymentItemID: zSchema.string(),
  })
  .passthrough();

export const contractorPaymentWebhookDeductionSchema = zSchema
  .object({
    deductionID: zSchema.string(),
  })
  .passthrough();

export const contractorPaymentWebhookSchema = zSchema.object({
  /**
   * @deprecated Use `totals.gross_amount` and `totals.net_amount` instead. This field is maintained for backward compatibility.
   */
  amount: zSchema.number().nullish(),
  approval_required: zSchema.boolean(),
  approved: zSchema.boolean(),
  companyID: schemes.companyID(),
  contractorID: schemes.mongoObjectId(),
  contractorPaymentID: schemes.mongoObjectId(),
  disbursement: zSchema.object({
    method: zSchema.nativeEnum(EContractorDisbursementMethod),
    history: zSchema
      .array(
        zSchema.object({
          date: zSchema.string(),
          status: zSchema.string(),
        })
      )
      .default([]),
    status: zSchema.string(),
  }),
  first_name: zSchema.string(),
  has_payment_items: zSchema.boolean(),
  /**
   * Present whenever `has_payment_items` is true, regardless of `operation_type`.
   * Omitted for legacy payments that use the `amount` field instead of payment items.
   */
  payment_items: zSchema
    .array(contractorPaymentWebhookPaymentItemSchema)
    .optional(),
  /** Present when the payment has one or more deductions applied. */
  deductions: zSchema.array(contractorPaymentWebhookDeductionSchema).optional(),
  last_name: zSchema.string(),
  metadata: schemes.metadata(),
  middle_name: zSchema.string().nullable(),
  pay_date: schemes.isoDate(),
  status: zSchema.nativeEnum(EContractorPaymentStatus),
  test: zSchema.boolean(),
  timestamp: zSchema.string(),
  /**
   * @deprecated Use `operation_type` instead. Maintained for backward compatibility.
   */
  type: zSchema.nativeEnum(EWebhookType),
  operation_type: zSchema.nativeEnum(EWebhookType),
  totals: zSchema.object({
    company_cash_requirement: zSchema.number().nullish(),
    company_debit: zSchema.number().nullish(),
    gross_amount: zSchema.number().nullish(),
    net_amount: zSchema.number().nullish(),
    deductions: zSchema.number().nullish(),
  }),
});

export type TContractorPaymentWebhook = zSchema.infer<
  typeof contractorPaymentWebhookSchema
>;
